import { useCallback, useMemo } from 'react'

import { isLanguage, Language } from '../../../types/language'
import { languageToLocale } from '../../../utils/languageToLocale'
import { InvoiceLineColumn } from '../../invoices/types/invoiceLineColumn'
import { isInvoiceProductType } from '../../products/utils/isInvoiceProductType'
import { FieldsInterface } from '../types/zervantSettingsModel'
import { ModelDocumentType } from '../types/zervantSettingsModelDocumentType'
import { getSettingsFieldsByDocumentType } from '../utils/getSettingsFieldsByDocumentType'
import { useZervantSettings } from './useZervantSettings'

export const useGetZervantDocumentSettings = (documentType: ModelDocumentType) => {
  const { data, isLoading } = useZervantSettings()

  const settingsFields = useMemo(() => getSettingsFieldsByDocumentType(data, documentType), [data, documentType])

  const getDefaultSettingsValue = useCallback(
    (fieldName: keyof FieldsInterface) => {
      const settingsFieldDefaultValue = settingsFields[fieldName]?.default

      if (fieldName === 'productType') {
        return isInvoiceProductType(settingsFieldDefaultValue) ? settingsFieldDefaultValue : undefined
      }

      return settingsFieldDefaultValue ? String(settingsFieldDefaultValue) : undefined
    },
    [settingsFields],
  )

  const getIsSettingsFieldVisible = useCallback(
    (fieldName: keyof FieldsInterface) => {
      return settingsFields[fieldName]?.visible
    },
    [settingsFields],
  )

  const getInvoiceLocale = useCallback(() => {
    const invoicingLanguage = getDefaultSettingsValue('language')
    const language: Language = isLanguage(invoicingLanguage) ? invoicingLanguage : 'en'

    return languageToLocale[language]
  }, [getDefaultSettingsValue])

  const getDocumentColumns = useCallback(() => {
    const columns = getDefaultSettingsValue('columns')
    return (columns?.split(',') || []) as InvoiceLineColumn[]
  }, [getDefaultSettingsValue])

  return { getDefaultSettingsValue, getDocumentColumns, getInvoiceLocale, getIsSettingsFieldVisible, isLoading }
}
